// src/screens/dashboard/Dashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  DollarSign, Target, BrainCircuit, Users, TrendingUp,
  Mail, Link as LinkIcon, ShoppingCart, CheckCircle2,
  RefreshCw, Inbox
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip 
} from 'recharts';
import '../dashboard/Dashboard.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/analytics/dashboard');
const CHARTS_API_URL = getApiUrl('/analytics/charts');


const getToken = () => localStorage.getItem('access_token') || '';



const formatCurrency = (val) => {
  if (val === null || val === undefined) return '$0';
  if (typeof val === 'string') return val;
  return `$${Number(val).toLocaleString()}`;
};


const formatCurrencyK = (val) => {
  if (val === null || val === undefined) return '$0k';
  if (typeof val === 'string') return val;
  return `$${Number(val).toLocaleString()}k`;
};


const formatPercent = (val) => {
  if (val === null || val === undefined) return '0%';
  return `${val}%`;
};


const formatAmountTick = (val) => {
  if (val === 0) return '$0';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 100000) return `$${(val / 1000).toFixed(0)}k`;
  return `$${Number(val).toLocaleString()}`;
};


const formatNumberTick = (val) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
  return `${Number(val).toLocaleString()}`;
};


const CHART_TABS = ['Revenue', 'DM Interactions', 'CTR', 'CTR → Purchase'];


const Dashboard = () => {
  const [chartTab, setChartTab] = useState('Revenue');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [chartsData, setChartsData] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartsError, setChartsError] = useState(null);


  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });


      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }


      const data = await res.json();
      setDashboardData(data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Unable to load dashboard data from the server.');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchCharts = useCallback(async () => {
    setChartsLoading(true);
    setChartsError(null);
    try {
      const token = getToken();
      const res = await fetch(CHARTS_API_URL, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const data = await res.json();
      setChartsData(data);
    } catch (err) {
      console.error('Charts fetch error:', err);
      // setChartsError('Unable to load chart data from the server.');
      setChartsData(null);
    } finally {
      setChartsLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchDashboard();
    fetchCharts();
  }, [fetchDashboard, fetchCharts]);


  const handleRefresh = useCallback(() => {
    fetchDashboard();
    fetchCharts();
  }, [fetchDashboard, fetchCharts]);


  const dmRevenue = dashboardData?.dm_revenue ?? 0;
  const dmRevenueGrowth = dashboardData?.dm_revenue_growth ?? 0;
  const convRate = dashboardData?.conv_rate ?? 0;
  const convRateGrowth = dashboardData?.conv_rate_growth ?? 0;
  const aiPredictionsCount = dashboardData?.ai_predictions_count ?? 0;
  const audienceTotal = dashboardData?.audience_total ?? '0';
  const audienceGrowth = dashboardData?.audience_growth ?? 0;

  const purchaseAfterFollowupsValue = dashboardData?.purchase_after_followups_value ?? 0;
  const purchaseAfterFollowupsGrowth = dashboardData?.purchase_after_followups_growth ?? 0;
  const abandonedOfferFollowupsRate = dashboardData?.abandoned_offer_followups_rate ?? 0;

  const funnelDiagnostics = dashboardData?.funnel_diagnostics ?? {};
  const overallEfficiency = funnelDiagnostics.overall_efficiency ?? 0;
  const dmOpen = funnelDiagnostics.dm_open ?? '0';
  const linkClick = funnelDiagnostics.link_click ?? '0';
  const checkout = funnelDiagnostics.checkout ?? '0';
  const purchase = funnelDiagnostics.purchase ?? '0';
  const linkClickDrop = funnelDiagnostics.link_click_drop ?? null;
  const checkoutDrop = funnelDiagnostics.checkout_drop ?? null;
  const purchaseDrop = funnelDiagnostics.purchase_drop ?? null;

  const highIntentCount = dashboardData?.high_intent_count ?? dashboardData?.intent?.high?.count ?? 0;
  const medIntentCount = dashboardData?.med_intent_count ?? dashboardData?.intent?.medium?.count ?? 0;
  const totalIntent = highIntentCount + medIntentCount;
  const highIntentPct = totalIntent > 0 ? Math.min(Math.round((highIntentCount / totalIntent) * 100), 100) : 0;
  const medIntentPct = totalIntent > 0 ? Math.min(Math.round((medIntentCount / totalIntent) * 100), 100) : 0;
  const predictedGrowth = dashboardData?.predicted_growth ?? purchaseAfterFollowupsGrowth ?? 0;

  const formatDrop = (drop) => {
    if (!drop) return null;
    return typeof drop === 'number' ? `${drop}% Drop` : drop.toString().includes('Drop') ? drop : `${drop} Drop`;
  };

  // Active chart dataset (Revenue / DM Interactions / CTR / CTR → Purchase)
  const activeChartValues = useMemo(() => {
    const datasets = chartsData?.datasets || dashboardData?.chart_data?.datasets || {};
    if (chartTab === 'CTR → Purchase' || chartTab === 'CTR-Purchase') {
      return datasets['CTR → Purchase'] || datasets['CTR-Purchase'] || datasets['CTR_Purchase'] || datasets['Purchase'] || [];
    }
    return datasets[chartTab] || [];
  }, [chartsData, dashboardData, chartTab]);

  const activeChartLabels = useMemo(() => {
    return chartsData?.labels || dashboardData?.chart_data?.labels || [];
  }, [chartsData, dashboardData]);

  const isAmountTab = chartTab === 'Revenue' || chartTab.includes('Purchase');
  const yAxisLabel = isAmountTab ? 'Amount' : 'Number';

  const chartData = useMemo(() => {
    if (!Array.isArray(activeChartValues) || activeChartValues.length === 0) {
      if (Array.isArray(activeChartLabels) && activeChartLabels.length > 0) {
        return activeChartLabels.map((lbl) => ({ time: lbl, value: 0 }));
      }
      return [];
    }

    return activeChartValues.map((val, idx) => {
      const time = activeChartLabels[idx] || `Day ${idx + 1}`;
      return {
        time,
        value: typeof val === 'number' ? val : Number(val) || 0,
      };
    });
  }, [activeChartValues, activeChartLabels]);

  const maxChartValue = useMemo(() => {
    if (!chartData || chartData.length === 0) return 0;
    return Math.max(...chartData.map((d) => d.value), 0);
  }, [chartData]);

  const yDomain = useMemo(() => {
    if (maxChartValue === 0) {
      return isAmountTab ? [0, 1000] : [0, 10];
    }
    return [0, 'auto'];
  }, [maxChartValue, isAmountTab]);

  const forecastChartData = useMemo(() => {
    if (Array.isArray(dashboardData?.forecast_data) && dashboardData.forecast_data.length > 0) {
      return dashboardData.forecast_data.map((item, idx) => ({
        time: item.time || item.name || item.label || item.month || `M${idx + 1}`,
        amount: Number(item.amount ?? item.value ?? item.revenue ?? 0),
      }));
    }
    if (Array.isArray(dashboardData?.revenue_forecast) && dashboardData.revenue_forecast.length > 0) {
      return dashboardData.revenue_forecast.map((item, idx) => ({
        time: item.time || item.name || item.label || item.month || `M${idx + 1}`,
        amount: Number(item.amount ?? item.value ?? item.revenue ?? 0),
      }));
    }

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    let target = 18500;
    if (purchaseAfterFollowupsValue > 0) {
      target = purchaseAfterFollowupsValue < 1000 ? purchaseAfterFollowupsValue * 1000 : purchaseAfterFollowupsValue;
    } else if (dmRevenue > 0) {
      const growthMult = 1 + (Math.max(predictedGrowth, 0) / 100);
      target = Math.round(dmRevenue * growthMult);
    }

    const multipliers = [0.08, 0.14, 0.22, 0.32, 0.44, 0.56, 0.68, 0.78, 0.86, 0.92, 0.97, 1.0];

    return months.map((month, idx) => ({
      time: month,
      amount: Math.round(target * multipliers[idx]),
    }));
  }, [dashboardData, purchaseAfterFollowupsValue, dmRevenue, predictedGrowth]);


  return (
    <>
      <Helmet>
        <title>Dashboard - Revexia AI Revenue Brain</title>
        <meta name="description" content="Revexia Dashboard - Track DM revenue, conversions, and AI predictions" />
      </Helmet>


      <div className="container-fluid px-4">


        {/* ── Refresh / Error Bar ── */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          {(error || chartsError) && (
            <div className="fallback-notice">
              <Inbox size={14} />
              <span>{error || chartsError}</span>
            </div>
          )}
          <button
            className="attribution-refresh-btn ms-auto"
            onClick={handleRefresh}
            disabled={loading || chartsLoading}
            aria-label="Refresh dashboard data"
          >
            <RefreshCw size={16} className={(loading || chartsLoading) ? 'spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>


        {/* ── KPI Cards ── */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-blue">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><DollarSign size={20} strokeWidth={2} /></div>
                <div className={`kpi-trend-badge ${dmRevenueGrowth >= 0 ? 'trend-up' : 'trend-down'}`}>
                  <TrendingUp size={12} /><span>{formatPercent(dmRevenueGrowth)}</span>
                </div>
              </div>
              <div className="kpi-label-text">DM REVENUE (30D)</div>
              <div className="kpi-value-text">
                {loading ? '—' : formatCurrency(dmRevenue)}
              </div>
              <div className="kpi-progress-track">
                <div
                  className="kpi-progress-fill progress-blue"
                  style={{ width: `${Math.min(Math.max(dmRevenueGrowth, 5), 100)}%` }}
                ></div>
              </div>
            </div>
          </div>


          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-purple">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><Target size={20} strokeWidth={2} /></div>
                <div className={`kpi-trend-badge ${convRateGrowth >= 0 ? 'trend-up' : 'trend-down'}`}>
                  <TrendingUp size={12} /><span>{formatPercent(convRateGrowth)}</span>
                </div>
              </div>
              <div className="kpi-label-text">CONVERSION RATE</div>
              <div className="kpi-value-text">
                {loading ? '—' : formatPercent(convRate)}
              </div>
              <div className="kpi-progress-track">
                <div
                  className="kpi-progress-fill progress-purple"
                  style={{ width: `${Math.min(Math.max(convRate, 5), 100)}%` }}
                ></div>
              </div>
            </div>
          </div>


          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-orange">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><BrainCircuit size={20} strokeWidth={2} /></div>
                <div className="kpi-trend-badge trend-up">
                  <TrendingUp size={12} /><span>AI</span>
                </div>
              </div>
              <div className="kpi-label-text">AI PREDICTIONS</div>
              <div className="kpi-value-text">
                {loading ? '—' : aiPredictionsCount}
              </div>
              <div className="kpi-progress-track">
                <div
                  className="kpi-progress-fill progress-orange"
                  style={{ width: `${Math.min(Math.max(aiPredictionsCount, 5), 100)}%` }}
                ></div>
              </div>
            </div>
          </div>


          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-cyan">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><Users size={20} strokeWidth={2} /></div>
                <div className={`kpi-trend-badge ${audienceGrowth >= 0 ? 'trend-up' : 'trend-down'}`}>
                  <TrendingUp size={12} /><span>{formatPercent(audienceGrowth)}</span>
                </div>
              </div>
              <div className="kpi-label-text">AUDIENCE TOTAL</div>
              <div className="kpi-value-text">
                {loading ? '—' : audienceTotal}
              </div>
              <div className="kpi-progress-track">
                <div
                  className="kpi-progress-fill progress-cyan"
                  style={{ width: `${Math.min(Math.max(audienceGrowth, 5), 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>


        {/* ── Revenue Chart ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="chart-card-container">
              <div className="chart-card-header">
                <div className="chart-header-info">
                  <h2 className="chart-card-title">
                    Revenue Performance
                    <span className="realtime-badge">REAL-TIME</span>
                  </h2>
                  <p className="chart-card-subtitle">Cross-channel attribution analysis</p>
                </div>
                <div className="chart-tab-group">
                  {CHART_TABS.map(tab => (
                    <button
                      key={tab}
                      className={`chart-tab-btn ${chartTab === tab ? 'active' : ''}`}
                      onClick={() => setChartTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
              <div className="chart-svg-wrapper">
                {chartsLoading ? (
                  <div className="attribution-state-card">
                    <div className="loading-spinner"></div>
                    <p>Loading chart data...</p>
                  </div>
                ) : chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 15, bottom: 25 }}
                    >
                      <defs>
                        <linearGradient id="revenuePerformanceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity="0.35" />
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity="0.02" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        vertical={false} 
                        stroke="rgba(255, 255, 255, 0.06)" 
                      />
                      <XAxis
                        dataKey="time"
                        axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                        tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                        tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11, fontWeight: 600 }}
                        dy={8}
                        minTickGap={20}
                        label={{ 
                          value: 'Time', 
                          position: 'insideBottom', 
                          offset: -15, 
                          fill: 'rgba(255, 255, 255, 0.45)', 
                          fontSize: 11, 
                          fontWeight: 700,
                          letterSpacing: '0.08em'
                        }}
                      />
                      <YAxis
                        axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                        tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                        tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11, fontWeight: 600 }}
                        tickFormatter={isAmountTab ? formatAmountTick : formatNumberTick}
                        domain={yDomain}
                        width={70}
                        dx={-2}
                        label={{ 
                          value: yAxisLabel, 
                          angle: -90, 
                          position: 'insideLeft', 
                          fill: 'rgba(255, 255, 255, 0.45)', 
                          fontSize: 11, 
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          style: { textAnchor: 'middle' },
                          dx: -2
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(13, 15, 41, 0.95)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: '12px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                          backdropFilter: 'blur(8px)',
                          padding: '10px 14px',
                        }}
                        labelStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}
                        formatter={(value) => [
                          isAmountTab ? `$${Number(value).toLocaleString()}` : Number(value).toLocaleString(),
                          yAxisLabel
                        ]}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#3B82F6"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#revenuePerformanceGradient)"
                        dot={{ r: 3, fill: '#3B82F6', stroke: '#0D0F29', strokeWidth: 1.5 }}
                        activeDot={{ r: 6, fill: '#60A5FA', stroke: '#FFFFFF', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data-block">
                    <Inbox size={20} />
                    <p>No {chartTab} data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>


        {/* ── AI Intent & Forecast ── */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-lg-5">
            <div className="ai-intent-container">
              <div className="ai-intent-header">
                <div className="intent-title-group">
                  <div className="intent-icon-wrapper"><BrainCircuit size={22} strokeWidth={2} /></div>
                  <span className="intent-title-text">AI Purchase Intent</span>
                </div>
                <span className="live-status-badge">LIVE</span>
              </div>
              <div className="funnel-visualization">
                <div className="funnel-pyramid">
                  <div className="funnel-dot dot-1"></div>
                  <div className="funnel-dot dot-2"></div>
                  <div className="funnel-dot dot-3"></div>
                  <div className="funnel-dot dot-4"></div>
                  <div className="funnel-dot dot-5"></div>
                </div>
              </div>
              <div className="forecast-display">
                <p className="forecast-label-text">90-DAY FORECAST</p>
                <div className="forecast-amount-wrapper">
                  <span className="forecast-amount-value">
                    {loading ? '—' : formatCurrencyK(purchaseAfterFollowupsValue)}
                  </span>
                  <span className="forecast-potential-text">
                    potential · {formatPercent(purchaseAfterFollowupsGrowth)} growth
                  </span>
                </div>
              </div>
              <div className="intent-metrics-list">
                <div className="intent-metric-item">
                  <div className="metric-item-header">
                    <span className="metric-label">High Intent</span>
                    <span className="metric-count metric-green">
                      {loading ? '—' : Number(highIntentCount).toLocaleString()}
                    </span>
                  </div>
                  <div className="metric-progress-track">
                    <div className="metric-progress-bar bar-green" style={{ width: `${highIntentPct}%` }}></div>
                  </div>
                </div>
                <div className="intent-metric-item">
                  <div className="metric-item-header">
                    <span className="metric-label">Med Intent</span>
                    <span className="metric-count metric-orange">
                      {loading ? '—' : Number(medIntentCount).toLocaleString()}
                    </span>
                  </div>
                  <div className="metric-progress-track">
                    <div className="metric-progress-bar bar-orange" style={{ width: `${medIntentPct}%` }}></div>
                  </div>
                </div>
                <div className="intent-metric-item">
                  <div className="metric-item-header">
                    <span className="metric-label">Abandoned Follow-ups</span>
                    <span className="metric-count metric-orange">
                      {loading ? '—' : formatPercent(abandonedOfferFollowupsRate)}
                    </span>
                  </div>
                  <div className="metric-progress-track">
                    <div
                      className="metric-progress-bar bar-orange"
                      style={{ width: `${Math.min(Math.max(abandonedOfferFollowupsRate, 5), 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-7">
            <div className="forecast-card-container">
              <div className="forecast-card-header">
                <div className="forecast-header-info">
                  <h3 className="forecast-card-title">90-DAY REVENUE FORECAST</h3>
                  <p className="forecast-card-subtitle">AI modeling of projected multi-quarter growth</p>
                </div>
                <div className="growth-badge">
                  <TrendingUp size={14} />
                  <span>
                    PREDICTED GROWTH {loading ? '—' : predictedGrowth >= 0 ? `+${predictedGrowth}%` : `${predictedGrowth}%`}
                  </span>
                </div>
              </div>
              <div className="forecast-chart-wrapper">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart
                    data={forecastChartData}
                    margin={{ top: 15, right: 25, left: 15, bottom: 25 }}
                  >
                    <defs>
                      <linearGradient id="dashboardForecastGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity="0.35" />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity="0.02" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      vertical={false} 
                      stroke="rgba(255, 255, 255, 0.06)" 
                    />
                    <XAxis
                      dataKey="time"
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11, fontWeight: 600 }}
                      dy={8}
                      label={{ 
                        value: 'Time', 
                        position: 'insideBottom', 
                        offset: -15, 
                        fill: 'rgba(255, 255, 255, 0.45)', 
                        fontSize: 11, 
                        fontWeight: 700,
                        letterSpacing: '0.08em'
                      }}
                    />
                    <YAxis
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11, fontWeight: 600 }}
                      tickFormatter={formatAmountTick}
                      width={70}
                      dx={-2}
                      label={{ 
                        value: 'Amount', 
                        angle: -90, 
                        position: 'insideLeft', 
                        fill: 'rgba(255, 255, 255, 0.45)', 
                        fontSize: 11, 
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        style: { textAnchor: 'middle' },
                        dx: -2
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(13, 15, 41, 0.95)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(8px)',
                        padding: '10px 14px',
                      }}
                      labelStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}
                      formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Amount']}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#dashboardForecastGradient)"
                      dot={{ r: 3, fill: '#3B82F6', stroke: '#0D0F29', strokeWidth: 1.5 }}
                      activeDot={{ r: 6, fill: '#60A5FA', stroke: '#FFFFFF', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>



        {/* ── Funnel Diagnostics ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="funnel-diagnostics-card">
              <div className="diagnostics-header">
                <div className="diagnostics-header-left">
                  <h2 className="diagnostics-title">Funnel Diagnostics</h2>
                  <p className="diagnostics-subtitle">End-to-end attribution mapping</p>
                </div>
                <div className="diagnostics-efficiency">
                  <p className="efficiency-label">OVERALL EFFICIENCY</p>
                  <p className="efficiency-value">{loading ? '—' : formatPercent(overallEfficiency)}</p>
                </div>
              </div>
              <div className="funnel-flow-container">
                <div className="flow-line"></div>
                <div className="flow-steps-wrapper">
                  <div className="flow-step-item">
                    <div className="flow-step-icon"><Mail size={24} strokeWidth={2.5} /></div>
                    <div className="flow-step-label">DM Open</div>
                    <div className="flow-step-value">{loading ? '—' : dmOpen}</div>
                  </div>
                  <div className="flow-step-item">
                    {formatDrop(linkClickDrop) && <div className="flow-drop-tooltip">{formatDrop(linkClickDrop)}</div>}
                    <div className="flow-step-icon"><LinkIcon size={24} strokeWidth={2.5} /></div>
                    <div className="flow-step-label">Link Click</div>
                    <div className="flow-step-value">{loading ? '—' : linkClick}</div>
                  </div>
                  <div className="flow-step-item">
                    {formatDrop(checkoutDrop) && <div className="flow-drop-tooltip">{formatDrop(checkoutDrop)}</div>}
                    <div className="flow-step-icon"><ShoppingCart size={24} strokeWidth={2.5} /></div>
                    <div className="flow-step-label">Checkout</div>
                    <div className="flow-step-value">{loading ? '—' : checkout}</div>
                  </div>
                  <div className="flow-step-item">
                    {formatDrop(purchaseDrop) && <div className="flow-drop-tooltip">{formatDrop(purchaseDrop)}</div>}
                    <div className="flow-step-icon flow-active">
                      <CheckCircle2 size={24} strokeWidth={2.5} />
                      <span className="pulse-ring"></span>
                    </div>
                    <div className="flow-step-label">Purchase</div>
                    <div className="flow-step-value highlight">{loading ? '—' : purchase}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>
    </>
  );
};


export default Dashboard;